import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";

const steps = [
  {
    title: "Welcome to PREPT!",
    description: "Let's take a quick tour to help you get started with managing your production workflow.",
    icon: "👋",
  },
  {
    title: "Create Projects",
    description: "Start by creating a project. Add your production details, budget, and important dates all in one place.",
    icon: "📋",
  },
  {
    title: "Manage Equipment",
    description: "Keep track of all your gear. Add items, track costs, and organize them by categories.",
    icon: "🎥",
  },
  {
    title: "Crew Management",
    description: "Add and manage your crew contacts. Easily add them to projects and collaborate by sharing access.",
    icon: "👥",
  },
  {
    title: "Ready to Begin!",
    description: "You're all set to start using PREPT. Click 'Get Started' to begin organizing your production.",
    icon: "🚀",
  },
];

export function OnboardingDialog() {
  const [open, setOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useAuth();

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", user?.id);

      if (error) throw error;
      
      setOpen(false);
      toast.success("Welcome to PREPT! You're ready to get started.");
    } catch (error) {
      console.error("Error updating onboarding status:", error);
      toast.error("There was an error completing the onboarding.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <div className="grid gap-6 py-4">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="text-4xl">{steps[currentStep].icon}</div>
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight">
                {steps[currentStep].title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {steps[currentStep].description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 flex gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    index === currentStep
                      ? "bg-primary"
                      : index < currentStep
                      ? "bg-primary/50"
                      : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="w-[100px]"
            >
              Skip
            </Button>
            <Button onClick={handleNext} className="w-[100px]">
              {currentStep === steps.length - 1 ? "Get Started" : "Next"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}